import { ErrorInformation } from '@shared/models/error/error-information';
import { InjectionToken } from '@angular/core';
import {
  BasicCrudActions,
  CreateAction,
  DeleteAction,
  LoadAllAction,
  LoadOneAction,
  ResetAction,
  UpdateAction
} from '@/types/action.types';
import { AsyncResult } from '@common/result/asyncResult';
import { PageFeature } from '@ervo/page-framework';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { UserGroupEntity } from '@/app/platform/platform-data/models/user-group.entity';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';
import { UserAccountEntity } from '@/app/platform/platform-data/models/user-account.entity';
import { DeviceEntity } from '@/app/platform/platform-data/models/device.entity';
import { FaultyClickTroughConfiguration } from '@/app/platform/platform-data/synchronizers/types';
import { PresentDeviceUserFilters } from '@/app/platform/platform-data/services/types';

export const EGI_APP_ACTIONS = new InjectionToken<EgiAppActions>('EgiAppActions');

export interface EgiAppActions
  extends LoadAllAction<EgiAppEntity>,
    LoadOneAction<EgiAppEntity>,
    CreateAction<EgiAppEntity>,
    DeleteAction<EgiAppEntity>,
    UpdateAction<EgiAppEntity>,
    ResetAction {
  reOrder(app: EgiAppEntity): AsyncResult<EgiAppEntity[], ErrorInformation>;

  synchronizeAll(): AsyncResult<
    EgiAppEntity[],
    ErrorInformation | ErrorInformation<FaultyClickTroughConfiguration[]>
  >;
}

export const NAVIGATION_ITEM_ACTIONS = new InjectionToken<NavigationItemActions>('');

export interface NavigationItemActions
  extends CreateAction<NavigationItemEntity>,
    DeleteAction<NavigationItemEntity>,
    UpdateAction<NavigationItemEntity> {
  createMenuItem(item: NavigationItemEntity): AsyncResult<NavigationItemEntity, ErrorInformation>;

  move(item: NavigationItemEntity): AsyncResult<NavigationItemEntity, ErrorInformation>;

  synchronizeAll(): AsyncResult<
    EgiAppEntity[],
    ErrorInformation | ErrorInformation<FaultyClickTroughConfiguration[]>
  >;

  changeConfiguration(i: NavigationItemEntity): AsyncResult<NavigationItemEntity, ErrorInformation>;
}

export const AUTHORIZATION_ACTIONS = new InjectionToken<AuthorizationActions>('');

export interface AuthorizationActions {
  linkAppToGroup(
    userGroup: UserGroupEntity,
    app: EgiAppEntity
  ): AsyncResult<UserGroupEntity, ErrorInformation>;

  unlinkAppFromGroup(
    userGroup: UserGroupEntity,
    app: EgiAppEntity
  ): AsyncResult<UserGroupEntity, ErrorInformation>;

  changeFeatureAuthorizations(
    group: UserGroupEntity,
    commands: ChangeAuthorizationCommand<PageFeature, NavigationItemEntity>[]
  ): AsyncResult<UserGroupEntity, ErrorInformation>;

  changeNavigationItemAuthorizations(
    group: UserGroupEntity,
    commands: ChangeAuthorizationCommand<NavigationItemEntity>[]
  ): AsyncResult<UserGroupEntity, ErrorInformation>;

  changeAuthorizations(
    group: UserGroupEntity,
    navigationItemCommands: ChangeAuthorizationCommand<NavigationItemEntity>[],
    featureCommands: ChangeAuthorizationCommand<PageFeature, NavigationItemEntity>[]
  ): AsyncResult<UserGroupEntity, ErrorInformation>;
}

export type AuthorizationCommandType = 'authorize' | 'unAuthorize';

export interface ChangeAuthorizationCommand<T, TExtra = undefined> {
  type: AuthorizationCommandType;
  item: T;
  extraItem?: TExtra;
}

export const USER_GROUP_ACTIONS = new InjectionToken<UserGroupActions>('');

export interface UserGroupActions
  extends BasicCrudActions<UserGroupEntity>,
    LoadOneAction<UserGroupEntity>,
    ResetAction {
  loadLinkedApps(userGroup: UserGroupEntity): AsyncResult<UserGroupEntity, ErrorInformation>;

  addMember(
    group: UserGroupEntity,
    account: UserAccountEntity
  ): AsyncResult<UserGroupEntity, ErrorInformation>;

  removeMember(
    group: UserGroupEntity,
    account: UserAccountEntity
  ): AsyncResult<UserGroupEntity, ErrorInformation>;
}

export const USER_ACCOUNT_ACTIONS = new InjectionToken<UserAccountActions>('');

export interface UserAccountActions extends LoadAllAction<UserAccountEntity> {}

export const DEVICE_ACTIONS = new InjectionToken<DeviceActions>('');

export interface DeviceActions extends BasicCrudActions<DeviceEntity>, ResetAction {}

export const PRESENT_DEVICE_USER_ACTIONS = new InjectionToken<PresentDeviceUserActions>('');

export interface PresentDeviceUserActions {
  startListening(): void;

  stopListening(): void;

  setFilters(filters: PresentDeviceUserFilters): Promise<void>;

  forceStop(userId: string): AsyncResult<boolean, ErrorInformation>;

  forceStopAll(): AsyncResult<boolean, ErrorInformation>;

  forceInvalidate(userId: string): AsyncResult<boolean, ErrorInformation>;

  forceInvalidateAll(): AsyncResult<boolean, ErrorInformation>;
}
